import {
  type InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { teamService } from '@/services/team-service';
import type { Team } from '@/types/core/team';
import type { Page } from '@/types/shared/page';
import { pollForResourceGone } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { teamMutationKeys, teamQueryKeys } from './query-keys';

/**
 * Hook to delete a team with optimistic updates.
 * Returns a react-query mutation that accepts a teamId.
 * Optimistically removes the team from the cache, then polls to verify deletion.
 */
export function useDeleteTeam(tenantId?: UUID) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: teamMutationKeys.deleteTeam(tenantId),
    mutationFn: async (teamId: UUID) => {
      if (!tenantId) throw new Error('Missing tenant id');

      const success = await teamService.deleteTeam(tenantId, teamId);
      if (!success) {
        throw new Error('Failed to delete team');
      }

      await pollForResourceGone(() => teamService.findTeam(tenantId, teamId));
      return teamId;
    },
    onMutate: async (teamId: UUID) => {
      if (!tenantId) return { previousData: [] };

      // Cancel any in-flight queries for teams
      await qc.cancelQueries({
        queryKey: teamQueryKeys.listByTenant(tenantId),
      });

      // Snapshot the current cache data for rollback
      const previousData = qc.getQueriesData<InfiniteData<Page<Team>>>({
        queryKey: teamQueryKeys.listByTenant(tenantId),
      });

      // Optimistically remove the team from cache
      qc.setQueriesData<InfiniteData<Page<Team>>>(
        { queryKey: teamQueryKeys.listByTenant(tenantId) },
        (old) => {
          if (!old) return old;

          return {
            ...old,
            pages: old.pages.map((page, index) => ({
              ...page,
              items: page.items.filter((team) => team.teamId !== teamId),
              // Decrement total count on the first page
              total: index === 0 ? (page.total ?? 0) - 1 : page.total,
            })),
          };
        }
      );

      return { previousData };
    },
    onError: (_error, _teamId, context) => {
      // Rollback: restore the previous cache data
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          qc.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: async () => {
      // Invalidate related queries to ensure consistency
      await qc.invalidateQueries({
        queryKey: teamQueryKeys.listByTenant(tenantId),
        refetchType: 'all',
      });
      // Also invalidate team members queries since they depend on team existence
      await qc.invalidateQueries({
        queryKey: teamQueryKeys.memberListByTenant(tenantId),
        refetchType: 'all',
      });
    },
  });
}
